import React, { useState, useContext, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Platform,
  SafeAreaView,
  ScrollView,
  useWindowDimensions,
} from "react-native";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { AuthContext } from "../Context/AuthContext";
import { NetworkContext } from "../Context/NetworkContext";
import GlobalStyles from "../components/StylenColor";
import { LinearGradient } from "expo-linear-gradient";

// Desktop web imports
import { useDesktopDetect, DesktopTheme } from "../components/web";

const { colors, typography, spacing, borderRadius, shadows } = DesktopTheme;

// Desktop Login View
const DesktopCompanyLoginView = ({
  email,
  setEmail,
  password,
  setPassword,
  isLoading,
  handleLogin,
  navigation,
}) => {
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  return (
    <View style={desktopStyles.container}>
      <View style={desktopStyles.splitContainer}>
        {/* Left Panel - Branding */}
        <View style={desktopStyles.brandPanel}>
          <View style={desktopStyles.brandContent}>
            <Image
              source={require("../assets/ESG_logo.png")}
              style={desktopStyles.logo}
              resizeMode="contain"
            />
            <Text style={desktopStyles.brandTitle}>ESG Tracker</Text>
            <Text style={desktopStyles.brandTagline}>
              Lighting the Way Towards Sustainability
            </Text>
            <View style={desktopStyles.featureList}>
              <View style={desktopStyles.featureItem}>
                <Text style={desktopStyles.featureIcon}>📊</Text>
                <Text style={desktopStyles.featureText}>Track ESG Performance</Text>
              </View>
              <View style={desktopStyles.featureItem}>
                <Text style={desktopStyles.featureIcon}>📈</Text>
                <Text style={desktopStyles.featureText}>Generate Reports</Text>
              </View>
              <View style={desktopStyles.featureItem}>
                <Text style={desktopStyles.featureIcon}>🎯</Text>
                <Text style={desktopStyles.featureText}>Set Sustainability Goals</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Right Panel - Login Form */}
        <View style={desktopStyles.formPanel}>
          <View style={desktopStyles.formContainer}>
            <Text style={desktopStyles.formTitle}>Company Login</Text>
            <Text style={desktopStyles.formSubtitle}>
              Sign in to access your ESG dashboard
            </Text>

            <View style={desktopStyles.inputGroup}>
              <Text style={desktopStyles.inputLabel}>Email Address</Text>
              <TextInput
                style={[
                  desktopStyles.input,
                  emailFocused && desktopStyles.inputFocused,
                ]}
                placeholder="company@example.com"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor={colors.textMuted}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
              />
            </View>

            <View style={desktopStyles.inputGroup}>
              <Text style={desktopStyles.inputLabel}>Password</Text>
              <TextInput
                style={[
                  desktopStyles.input,
                  passwordFocused && desktopStyles.inputFocused,
                ]}
                placeholder="Enter your password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholderTextColor={colors.textMuted}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
              />
            </View>

            {isLoading ? (
              <View style={desktopStyles.loadingButton}>
                <ActivityIndicator size="small" color="#fff" />
              </View>
            ) : (
              <TouchableOpacity
                style={desktopStyles.loginButton}
                onPress={handleLogin}
              >
                <Text style={desktopStyles.loginButtonText}>Sign In</Text>
              </TouchableOpacity>
            )}

            <View style={desktopStyles.divider}>
              <View style={desktopStyles.dividerLine} />
              <Text style={desktopStyles.dividerText}>or</Text>
              <View style={desktopStyles.dividerLine} />
            </View>

            <View style={desktopStyles.linkGroup}>
              <TouchableOpacity
                onPress={() => navigation.navigate("CompanyRegistrationScreen")}
                style={desktopStyles.linkButton}
              >
                <Text style={desktopStyles.linkText}>
                  Don't have an account?{" "}
                  <Text style={desktopStyles.linkTextBold}>Register Company</Text>
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => navigation.navigate("EmployeeLoginScreen")}
                style={desktopStyles.linkButton}
              >
                <Text style={desktopStyles.linkText}>
                  Employee?{" "}
                  <Text style={desktopStyles.linkTextBold}>Login Here</Text>
                </Text>
              </TouchableOpacity>
            </View>

            <View style={desktopStyles.footer}>
              <Text style={desktopStyles.footerText}>
                All rights reserved under Sowin Enterprises® 2025
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const CompanyLoginScreen = ({ navigation }) => {
  const { loginCompany } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { setCompanyId, setCompanyName } = useContext(AuthContext);
  const { isConnected } = useContext(NetworkContext);
  const { width } = useWindowDimensions();
  const isWide = width > 800;

  // Desktop detection
  const { isDesktopWeb } = useDesktopDetect();

  const validateInputs = () => {
    if (!email || !password) {
      showAlert("Error", "Both fields are required!");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showAlert("Error", "Please enter a valid email address.");
      return false;
    }

    return true;
  };

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const handleLogin = async () => {
    if (!validateInputs()) return;
    if (!isConnected) {
      showAlert("No Internet", "You're currently offline.");
      return;
    }

    setIsLoading(true);

    try {
      const q = query(
        collection(db, "companies"),
        where("email", "==", email.toLowerCase()),
        where("password", "==", password)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const companyDoc = querySnapshot.docs[0];
        const companyData = companyDoc.data();

        await loginCompany(companyDoc.id, companyData.name, companyData.email);

        navigation.reset({
          index: 0,
          routes: [{ name: "Drawer" }],
        });
      } else {
        showAlert("Error", "Invalid email or password!");
      }
    } catch (error) {
      console.error("Login error:", error);
      showAlert("Error", "Failed to login. Please try again!");
    } finally {
      setIsLoading(false);
    }
  };

  // Render desktop version
  if (isDesktopWeb) {
    return (
      <DesktopCompanyLoginView
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        isLoading={isLoading}
        handleLogin={handleLogin}
        navigation={navigation}
      />
    );
  }

  // Mobile version (unchanged)
  return (
    <LinearGradient
      colors={GlobalStyles.GlobalBg.gradientColors}
      style={styles.safeArea}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <View
          style={[styles.mainContainer, isWide && styles.mainContainerWide]}
        >
          {/* Left Column */}
          <View style={[styles.leftContainer, isWide && styles.leftWide]}>
            <Image
              source={require("../assets/ESG_logo.png")}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text
              style={[
                styles.tagline,
                { marginBottom: 4 },
                isWide && { fontSize: GlobalStyles.fontSize.sm },
              ]}
            >
              Lighting the Way
            </Text>
            <Text
              style={[
                styles.tagline,
                ,
                isWide && { fontSize: GlobalStyles.fontSize.sm },
              ]}
            >
              Towards Sustainability
            </Text>
          </View>

          {/* Right Column */}
          <View style={[styles.rightContainer, isWide && styles.rightWide]}>
            <View style={styles.formWrapper}>
              <Text
                style={[
                  styles.title,
                  GlobalStyles.titleTxtOffset,
                  isWide && { fontSize: GlobalStyles.fontSize.lg + 1 },
                ]}
              >
                Company Login
              </Text>

              <TextInput
                style={styles.input}
                placeholder="Email"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                selectionColor={GlobalStyles.inpColor.colors.selection}
              />

              <TextInput
                style={styles.input}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholderTextColor={GlobalStyles.inpColor.colors.placeholder}
                selectionColor={GlobalStyles.inpColor.colors.selection}
              />

              {isLoading ? (
                <ActivityIndicator
                  size="large"
                  color={GlobalStyles.btnColor.backgroundColor}
                  style={styles.loginButton}
                />
              ) : (
                <TouchableOpacity
                  onPress={handleLogin}
                  style={[styles.loginButton, GlobalStyles.btnBgShadow]}
                >
                  <Text
                    style={[
                      styles.loginButtonText,
                      GlobalStyles.btntxtoffset,
                      isWide && { fontSize: GlobalStyles.fontSize.sm },
                    ]}
                  >
                    Login
                  </Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                onPress={() => navigation.navigate("CompanyRegistrationScreen")}
                style={styles.registerLink}
              >
                <Text style={styles.registerText}>
                  Not registered? Register your company
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => navigation.navigate("EmployeeLoginScreen")}
                style={styles.registerLink}
              >
                <Text style={styles.registerText}>
                  For Employee Login. Click Here
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.footer}>
              <Text style={styles.footerText}>
                All rights are Reserved under Sowin Enterprises® 2025
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </LinearGradient>
  );
};

// Desktop styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.contentBg,
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    minHeight: '100vh',
  },

  // Brand Panel
  brandPanel: {
    flex: 1,
    backgroundColor: colors.sidebarBg,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
  },
  brandContent: {
    alignItems: 'center',
    maxWidth: 400,
  },
  logo: {
    width: 120,
    height: 120,
    marginBottom: spacing.lg,
  },
  brandTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h1,
    color: '#fff',
    marginBottom: spacing.sm,
  },
  brandTagline: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.h4,
    color: colors.sidebarTextMuted,
    textAlign: 'center',
    marginBottom: spacing.xxl,
  },
  featureList: {
    width: '100%',
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  featureIcon: {
    fontSize: 24,
    marginRight: spacing.md,
  },
  featureText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.sidebarText,
  },

  // Form Panel
  formPanel: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxl,
    backgroundColor: colors.cardBg,
  },
  formContainer: {
    width: '100%',
    maxWidth: 400,
  },
  formTitle: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.h2,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  formSubtitle: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textMuted,
    marginBottom: spacing.xl,
  },

  // Input
  inputGroup: {
    marginBottom: spacing.md,
  },
  inputLabel: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  input: {
    width: '100%',
    height: 48,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md,
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textPrimary,
    backgroundColor: colors.contentBg,
  },
  inputFocused: {
    borderColor: colors.primary,
    borderWidth: 2,
  },

  // Button
  loginButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.md,
    ...shadows.sm,
  },
  loadingButton: {
    width: '100%',
    height: 48,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.md,
    opacity: 0.7,
  },
  loginButtonText: {
    fontFamily: typography.fontFamily.heading,
    fontSize: typography.fontSize.body,
    color: '#fff',
  },

  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.lg,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,
  },
  dividerText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
    marginHorizontal: spacing.md,
  },

  // Links
  linkGroup: {
    alignItems: 'center',
  },
  linkButton: {
    paddingVertical: spacing.sm,
  },
  linkText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.body,
    color: colors.textSecondary,
  },
  linkTextBold: {
    fontFamily: typography.fontFamily.heading,
    color: colors.primary,
  },

  // Footer
  footer: {
    marginTop: spacing.xxl,
    alignItems: 'center',
  },
  footerText: {
    fontFamily: typography.fontFamily.body,
    fontSize: typography.fontSize.small,
    color: colors.textMuted,
  },
});

// Mobile styles (unchanged)
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "center",
    padding: 24,
  },
  mainContainer: {
    flexDirection: "column",
    alignItems: "center",
  },
  mainContainerWide: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: 1000,
    alignSelf: "center",
    minHeight: "80vh",
  },
  formWrapper: {
    width: "100%",
    maxWidth: 350,
    alignItems: "center",
    backgroundColor: "#bdd9c2",
    padding: 20,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  leftContainer: {
    alignItems: "center",
    marginBottom: 30,
  },
  leftWide: {
    width: "50%",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingHorizontal: 20,
  },
  rightContainer: {
    width: "100%",
    alignItems: "center",
  },
  rightWide: {
    width: "50%",
    paddingTop: 20,
    alignItems: "flex-end",
    justifyContent: "center",
    paddingHorizontal: 20,
  },
  logo: {
    width: 160,
    height: 160,
    marginBottom: 10,
  },
  tagline: {
    paddingVertical: 2,
    fontSize: GlobalStyles.fontSize.md,
    color: GlobalStyles.btnColor.backgroundColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    lineHeight: 25,
  },
  title: {
    fontSize: GlobalStyles.fontSize.xxxl,
    marginBottom: 25,
    color: GlobalStyles.btnColor.backgroundColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
  },
  input: {
    color: "#000",
    width: "100%",
    maxWidth: 350,
    fontSize: 16,
    height: 50,
    borderColor: "#e0e0e0",
    borderWidth: 1,
    borderRadius: 10,
    marginBottom: 18,
    paddingHorizontal: 15,
    backgroundColor: "#fff",
    fontFamily: GlobalStyles.inpFont.fontFamily,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  loginButton: {
    width: "100%",
    maxWidth: 350,
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center",
    marginBottom: 16,
  },
  loginButtonText: {
    color: "#fff",
    fontSize: GlobalStyles.fontSize.md,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  registerLink: {
    marginTop: 15,
  },
  registerText: {
    color: GlobalStyles.btnColor.backgroundColor,
    fontSize: GlobalStyles.fontSize.xss,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textDecorationLine: "underline",
  },
  footer: {
    alignItems: "center",
    marginTop: 50,
  },
  footerText: {
    fontSize: GlobalStyles.fontSize.xsss,
    color: "#777",
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
});

export default CompanyLoginScreen;
